import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { RenderIf } from 'react-rainbow-components';
import Button from 'components/Button';
import ButtonIcon from 'components/ButtonIcon';
import Close from 'components/icons/close';
import Gradient from 'components/Gradient';
import useSearchFilters from 'hooks/useSearchFilters';
import Calendar from 'components/icons/calendar';
import User from 'components/icons/user';
import StatusIcon from 'components/icons/status';
import { Customer } from 'data/firestore/agent/customer/types';
import Accordion from 'pages/Opportunities/View/accordion';
import {
    FilterHeader,
    Title,
    FiltersContainer,
    FiltersContent,
} from 'pages/Opportunities/View/styled';
import { INVOICE_FILTER_NAMES } from '../../constants';
import { InvoiceStatus } from '../../types';
import {
    StyledCustomerPicker,
    StyledDatePickerFilter,
    StyledStatusFilter,
} from './styled';

interface Params {
    organizationId: string;
    filterNames?: string[];
    hideCustomerFilter?: boolean;
    onRequestClose?: () => void;
}

const statusOptions: Array<{ value: InvoiceStatus; label: string }> = [
    { value: 'draft', label: 'Draft' },
    { value: 'sent', label: 'Sent' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'paid', label: 'Paid' },
    { value: 'void', label: 'Void' },
];

const Filters = ({
    organizationId,
    filterNames = INVOICE_FILTER_NAMES,
    hideCustomerFilter,
    onRequestClose = () => undefined,
}: Params) => {
    const {
        values,
        getValue: getFilterValue,
        setValue: setFilterValue,
        clear: clearFilters,
    } = useSearchFilters({ filterNames });

    const activeFiltersCount = useMemo(
        () => filterNames.reduce(
            (count, key) => (
                getFilterValue(key) !== null
                    ? count + 1
                    : count
            ),
            0,
        ),
        [filterNames, getFilterValue],
    );

    const issuedOnFilterValue = useMemo(
        () => {
            const { issuedOn } = values;
            if (!issuedOn) return undefined;
            return String(issuedOn).split(',').map((strDate) => DateTime.fromISO(strDate).toJSDate());
        },
        [values],
    );

    const handleChangeIssuedOn = useCallback(
        (newFilterValue: Date[]) => setFilterValue(
            'issuedOn',
            (
                newFilterValue.length === 0
                    ? null
                    : newFilterValue.map((date) => date.toISOString()).join(',')
            ),
        ),
        [setFilterValue],
    );

    const dueOnFilterValue = useMemo(
        () => {
            const { dueOn } = values;
            if (!dueOn) return undefined;
            return String(dueOn).split(',').map((strDate) => DateTime.fromISO(strDate).toJSDate());
        },
        [values],
    );

    const handleChangeDueOn = useCallback(
        (newFilterValue: Date[]) => setFilterValue(
            'dueOn',
            (
                newFilterValue.length === 0
                    ? null
                    : newFilterValue.map((date) => date.toISOString()).join(',')
            ),
        ),
        [setFilterValue],
    );

    const customerId = typeof values.customerId === 'string' ? values.customerId : undefined;

    const handleChangeCustomer = useCallback(
        (customer?: Customer | null) => setFilterValue(
            'customerId',
            customer?.id || null,
        ),
        [setFilterValue],
    );

    const statusFilterValue = values.status ? [String(values.status)] : [];

    const handleChangeStatus = useCallback(
        (selected: string[]) => {
            const current = values.status ? String(values.status) : undefined;
            const next = selected.find((value) => value !== current) || selected[0];
            setFilterValue('status', next || null);
        },
        [setFilterValue, values.status],
    );

    return (
        <FiltersContainer>
            <FilterHeader>
                <Title>
                    Filters
                    {' '}
                    <RenderIf isTrue={activeFiltersCount > 0}>
                        (
                        {activeFiltersCount}
                        )
                    </RenderIf>
                </Title>
                <div>
                    <Button
                        label="Clear"
                        variant="base"
                        size="small"
                        disabled={!activeFiltersCount}
                        borderRadius="semi-square"
                        onClick={clearFilters}
                    />
                    <ButtonIcon
                        borderRadius="semi-square"
                        size="small"
                        icon={<Close />}
                        onClick={onRequestClose}
                    />
                </div>
            </FilterHeader>
            <FiltersContent>
                <Accordion icon={<Calendar />} label="Issue date" name="issuedOn">
                    <StyledDatePickerFilter
                        onChange={handleChangeIssuedOn}
                        value={issuedOnFilterValue}
                    />
                </Accordion>
                <Accordion icon={<Calendar />} label="Due date" name="dueOn">
                    <StyledDatePickerFilter
                        onChange={handleChangeDueOn}
                        value={dueOnFilterValue}
                    />
                </Accordion>
                <RenderIf isTrue={!hideCustomerFilter}>
                    <Accordion icon={<User />} label="Customer" name="customerId">
                        <StyledCustomerPicker
                            agentId={organizationId}
                            placeholder="All customers"
                            value={customerId}
                            onChange={handleChangeCustomer}
                            disableCreate
                        />
                    </Accordion>
                </RenderIf>
                <Accordion icon={<StatusIcon />} label="Status" name="status">
                    <StyledStatusFilter
                        hideLabel
                        options={statusOptions}
                        value={statusFilterValue}
                        onChange={handleChangeStatus}
                    />
                </Accordion>
            </FiltersContent>
            <Gradient position="bottom" />
        </FiltersContainer>
    );
};

export default Filters;
